/**
 * CredentialsFooterColumn — regulated-industry credentials footer column.
 *
 * Slotted into <MarketingFooter>'s `extraColumns` slot. Used today on
 * protector-web (Oregon insurance broker — License, NPN, DRLP, E&O, OR
 * SoS Registry). Promoted to @puffin-core/marketing-shell on first
 * creation per the Gen-0 factory-first lens — any future regulated
 * tenant (health-broker, financial-services, etc.) reuses the same shape.
 *
 * Every field is optional — graceful degradation when partial data is
 * provided. When NO fields are provided, the entire column is omitted
 * (the caller can blindly pass props without conditional rendering).
 *
 * Pure data via props. Consumer holds all i18n. CSS-001 compliant —
 * shares the canonical .footer-column-list family with sibling columns
 * for layout parity. Per-field identification is a DATA ATTRIBUTE
 * (`data-credential-field`), matching this component's own sibling idiom
 * (`data-column-key` on the wrapper): the fields are targeting hooks, not
 * appearance variants — `.footer-column-list-credentials li` already
 * carries the entire visual treatment of every field, and no per-field
 * differentiation is possible or wanted for five "label: value" strings.
 *
 * @module @puffin-core/marketing-shell/CredentialsFooterColumn
 */

import PropTypes from 'prop-types';

const FIELD_DEFS = Object.freeze([
  { key: 'license', field: 'license' },
  { key: 'npn', field: 'npn' },
  { key: 'drlp', field: 'drlp' },
  { key: 'eAndO', field: 'eando' },
  { key: 'registry', field: 'registry' },
]);

/**
 * @param {Object} props
 * @param {string} props.heading       Column heading (e.g. "Credentials & Compliance").
 * @param {string} [props.license]
 * @param {string} [props.npn]
 * @param {string} [props.drlp]
 * @param {string} [props.eAndO]
 * @param {string} [props.registry]
 */
export default function CredentialsFooterColumn({ heading, license, npn, drlp, eAndO, registry }) {
  const fields = FIELD_DEFS.map((def) => ({
    ...def,
    value: { license, npn, drlp, eAndO, registry }[def.key],
  })).filter((f) => Boolean(f.value));

  if (fields.length === 0) {
    return null;
  }

  const headingId = 'footer-credentials-heading';

  return (
    <div className="footer-column-list" data-column-key="credentials">
      <h3 id={headingId} className="footer-column-title">
        {heading}
      </h3>
      <ul className="footer-column-list-credentials" aria-labelledby={headingId}>
        {fields.map((field) => (
          <li key={field.key} data-credential-field={field.field}>
            {field.value}
          </li>
        ))}
      </ul>
    </div>
  );
}

CredentialsFooterColumn.propTypes = {
  heading: PropTypes.string.isRequired,
  license: PropTypes.string,
  npn: PropTypes.string,
  drlp: PropTypes.string,
  eAndO: PropTypes.string,
  registry: PropTypes.string,
};
